<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'

import EditorTextAi from '@/components/atoms/EditorTextAi.vue'
import EditorTextBold from '@/components/beta-editor/text-toolbar/BetaEditorTextBold.vue'
import EditorTextBulletList from '@/components/beta-editor/text-toolbar/BetaEditorTextBulletList.vue'
import EditorTextEmoji from '@/components/atoms/EditorTextEmoji.vue'
import EditorTextItalic from '@/components/beta-editor/text-toolbar/BetaEditorTextItalic.vue'
import EditorTextOrderedList from '@/components/beta-editor/text-toolbar/BetaEditorTextOrderedList.vue'
import EditorTextStrike from '@/components/beta-editor/text-toolbar/BetaEditorTextStrike.vue'
import EditorTextStyle from '@/components/beta-editor/text-toolbar/BetaEditorTextStyle.vue'
import EditorTextUnderline from '@/components/beta-editor/text-toolbar/BetaEditorTextUnderline.vue'
import EditorTextLink from '@/components/beta-editor/text-toolbar/BetaEditorTextLink.vue'
import IconSettingLayout from '@/components/atoms/icons/IconSettingLayout.vue'
import IconSettingText from '@/components/atoms/icons/IconSettingText.vue'
import SettingImageSelector from '@/components/beta-editor/settings/controls/BetaSettingImageSelector.vue'
import SettingMargin from '@/components/atoms/SettingMargin.vue'
import SettingOpacity from '@/components/atoms/SettingOpacity.vue'
import SettingPadding from '@/components/atoms/SettingPadding.vue'
import SettingWidth from '@/components/atoms/SettingWidth.vue'
import EditorSidebarLayout from '@/components/layouts/EditorSidebarLayout.vue'
import SettingsLayout from '@/components/layouts/SettingsLayout.vue'
import BetaSettingAlignOptions from '@/components/beta-editor/settings/controls/BetaSettingAlignOptions.vue'
import BetaSettingColorPicker from '@/components/beta-editor/settings/controls/BetaSettingColorPicker.vue'
import BetaSettingFontSize from '@/components/beta-editor/settings/controls/BetaSettingFontSize.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useLayoutStore } from '@/stores/layout'

const betaStore = useEditorBetaStore()
const layoutStore = useLayoutStore()

const elementForEdit = computed(() => betaStore.elementForEdit)
const activeTextEditor = computed(() => betaStore.activeTextEditor)
const activeTextSelectionState = computed(() => betaStore.activeTextSelectionState)
const settings = ref([
    { id: 1, name: 'text', icon: markRaw(IconSettingText) },
    { id: 2, name: 'layout', icon: markRaw(IconSettingLayout) },
])

const backgroundColor = computed(() => (
    elementForEdit.value?.extra?.background_color
    || layoutStore.getSectionBackgroundColorDefault(elementForEdit.value?.sectionIndex ?? 0)
    || ''
))
const backgroundImage = computed(() => elementForEdit.value?.extra?.background_image ?? null)
const backgroundImageOpacity = computed(() => elementForEdit.value?.extra?.background_image_opacity ?? '80')
const backgroundFocalPoint = computed(() => (
    elementForEdit.value?.extra?.background_image_focal_point ?? { x: 50, y: 50 }
))
const activeTextFontSize = computed(() => activeTextSelectionState.value.fontSize ?? '16px')

const onChangeTextAlign = (alignment: 'left' | 'center' | 'right' | 'justify') => {
    if (!activeTextEditor.value) return

    if (activeTextEditor.value.isActive({ textAlign: alignment })) {
        betaStore.getTextEditorCommandChain(activeTextEditor.value)?.unsetTextAlign().run()
        return
    }

    betaStore.getTextEditorCommandChain(activeTextEditor.value)?.setTextAlign(alignment).run()
}

const onChangeTextFontSize = (event: any) => {
    if (!activeTextEditor.value || event.target !== 'font-size') return

    betaStore.getTextEditorCommandChain(activeTextEditor.value)?.setFontSize(event.value).run()
}

const onChangeBackgroundColor = (color: string) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        background_color: color,
    }

    betaStore.updateElementForEdit()
}

const onChangeImageSelector = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'image') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            background_image: event.value
                ? {
                    id: event.value.id,
                    uri: event.value.content,
                    width: event.value.width,
                    height: event.value.height,
                }
                : {
                    id: null,
                    uri: null,
                    width: null,
                    height: null,
                },
        }
    }

    if (event.target === 'focal-point') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            background_image_focal_point: event.value,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangeBackgroundImageOpacity = (opacity: number) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        background_image_opacity: opacity,
    }

    betaStore.updateElementForEdit()
}

const onChangeWidth = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'desktop') elementForEdit.value.grid = event.value
    if (event.target === 'mobile') elementForEdit.value.gridMobile = event.value

    betaStore.updateElementForEdit()
}

const onChangeSpacing = (event: any, target: 'margin' | 'padding') => {
    if (!elementForEdit.value) return

    if (event.config === 'desktop') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [target]: {
                ...elementForEdit.value.extra?.[target],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'mobile') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile`]: {
                ...elementForEdit.value.extra?.[`${target}_mobile`],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile_lock`]: event.value,
        }
    }

    betaStore.updateElementForEdit()
}
</script>

<template>
    <editor-sidebar-layout v-if="elementForEdit" :settings="settings">
        <template #text>
            <settings-layout name="Formatação do Texto" :default-open="true">
                <div class="space-y-2">
                    <span class="text-sm font-semibold text-[#060606] opacity-50">Alinhamento</span>

                    <beta-setting-align-options
                        :value="activeTextSelectionState.textAlign"
                        :disabled="!activeTextEditor"
                        :alignments="['left', 'center', 'right', 'justify']"
                        @change="onChangeTextAlign"
                    />
                </div>

                <p class="text-sidebar-toolbar__helper">
                    Selecione o texto para formatá-lo.
                </p>

                <div v-if="activeTextEditor" class="text-sidebar-toolbar">
                    <beta-setting-font-size
                        label="Tamanho da Fonte"
                        :value="activeTextFontSize"
                        :value-default="activeTextFontSize"
                        :lock="false"
                        :show-mode-control="false"
                        @change="onChangeTextFontSize"
                    />

                    <div class="text-sidebar-toolbar__row">
                        <editor-text-bold :editor="activeTextEditor" :is-dark="false" :active="activeTextSelectionState.bold" />
                        <editor-text-italic :editor="activeTextEditor" :is-dark="false" :active="activeTextSelectionState.italic" />
                        <editor-text-underline :editor="activeTextEditor" :is-dark="false" :active="activeTextSelectionState.underline" />
                        <editor-text-strike :editor="activeTextEditor" :is-dark="false" :active="activeTextSelectionState.strike" />
                    </div>

                    <div class="text-sidebar-toolbar__row">
                        <editor-text-style
                            :editor="activeTextEditor"
                            :is-dark="false"
                            background-color="#FAFAFA"
                            in-sidebar
                        />
                        <editor-text-link :editor="activeTextEditor" :is-dark="false" />
                        <editor-text-emoji :editor="activeTextEditor" :is-dark="false" />
                    </div>

                    <div class="text-sidebar-toolbar__row">
                        <editor-text-bullet-list :editor="activeTextEditor" :is-dark="false" :active="activeTextSelectionState.bulletList" />
                        <editor-text-ordered-list :editor="activeTextEditor" :is-dark="false" :active="activeTextSelectionState.orderedList" />
                        <editor-text-ai :editor="activeTextEditor" :is-dark="false" :element="elementForEdit" :schema="elementForEdit.schema" />
                    </div>
                </div>

                <div v-else class="text-sidebar-toolbar__empty">
                    Selecione o campo de texto que deseja editar.
                </div>
            </settings-layout>

            <settings-layout name="Configurações do Fundo" :default-open="true">
                <beta-setting-color-picker
                    label="Cor de Fundo"
                    :value="backgroundColor"
                    :default-value="layoutStore.getSectionBackgroundColorDefault(elementForEdit.sectionIndex) ?? ''"
                    clear-on-default
                    @change="onChangeBackgroundColor"
                />

                <setting-image-selector
                    label="Imagem de Fundo"
                    :value="backgroundImage?.uri"
                    with-focal-point
                    with-remove
                    :focal-point="backgroundFocalPoint"
                    @change="onChangeImageSelector"
                />

                <setting-opacity
                    v-if="backgroundImage?.uri"
                    label="Opacidade da imagem"
                    :value="backgroundImageOpacity"
                    @change="onChangeBackgroundImageOpacity"
                />
            </settings-layout>
        </template>

        <template #layout>
            <settings-layout name="Configurações do Conteúdo" :default-open="true">
                <setting-width
                    label="Largura"
                    :value="elementForEdit.grid"
                    :value-mobile="elementForEdit.gridMobile"
                    :values-permission-desktop="[4, 6, 12]"
                    :values-permission-mobile="[4, 6, 12]"
                    @change="onChangeWidth"
                />
            </settings-layout>

            <settings-layout name="Avançado">
                <setting-margin
                    label="Margem Externa"
                    :value="elementForEdit.extra?.margin"
                    :value-mobile="elementForEdit.extra?.margin_mobile"
                    :lock="elementForEdit.extra?.margin_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'margin')"
                />

                <setting-padding
                    label="Margem Interna"
                    :value="elementForEdit.extra?.padding"
                    :value-mobile="elementForEdit.extra?.padding_mobile"
                    :lock="elementForEdit.extra?.padding_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'padding')"
                />
            </settings-layout>
        </template>
    </editor-sidebar-layout>
</template>

<style scoped>
.text-sidebar-toolbar {
    @apply flex flex-col gap-3;
}

.text-sidebar-toolbar__helper {
    @apply mb-3 text-xs leading-5 text-[#8A94A6];
}

.text-sidebar-toolbar__row {
    @apply flex flex-wrap items-center justify-between gap-1.5 rounded-2xl border border-[#DCE5F2] bg-white px-3 py-2.5;
}

.text-sidebar-toolbar__row :deep(button) {
    @apply rounded-xl px-2 py-2 transition-colors duration-200 hover:bg-[#F3F7FD];
}

.text-sidebar-toolbar__row :deep(button.is-active) {
    @apply bg-[#EEF4FF] shadow-[inset_0_0_0_1px_#CFE0FF];
}

.text-sidebar-toolbar__row :deep(.relative > button) {
    @apply w-full justify-between;
}

.text-sidebar-toolbar__empty {
    @apply rounded-2xl border border-dashed border-[#DCE5F2] bg-white px-4 py-4 text-sm text-[#6C7E99];
}
</style>
